import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { FiCheckCircle, FiSearch } from "react-icons/fi";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, SelectField, TextAreaField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import SaaSNotFoundState from "../../components/common/saas/SaaSNotFoundState";
import { showToast } from "../../components/common/notification/Notification";
import { getErrorText } from "./services/partnerNetworkService";
import { useListRecord, useNetworkService } from "./hooks/useNetworkRecords";
import { PARTNERS_HOME, SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS } from "./constants/networkEndpoints";
import { fullName, isAlreadyAdded, PARTNER_SPECIALIZATIONS, toSimpleOptions } from "./utils/people";
import PersonAvatar from "./components/PersonAvatar";
import "./partnerNetwork.css";

const FORM_ID = "partner-form";
const NOTES_MAX = 2000;
const SPECIALIZATION_OPTIONS = toSimpleOptions(PARTNER_SPECIALIZATIONS);

// Same rules and messages as the mobile Add Partner form. Mobile has no validation on Edit, so there only the
// required fields are checked.
const nameRule = (label) => Yup.string().trim().required(`${label} is required`);
const addSchema = Yup.object({
  first_name: nameRule("First name").min(2, "First name must be at least 2 characters long"),
  last_name: nameRule("Last name").min(2, "Last name must be at least 2 characters long"),
  mobile_number: Yup.string().required("Mobile number is required").matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
  email_address: Yup.string(),
  company: Yup.string().max(255, "Company must be at most 255 characters"),
  notes: Yup.string().max(NOTES_MAX, `Notes must be at most ${NOTES_MAX} characters`),
});
const editSchema = Yup.object({
  first_name: nameRule("First name"),
  last_name: nameRule("Last name"),
  mobile_number: Yup.string().trim().required("Mobile number is required"),
  email_address: Yup.string(),
  company: Yup.string().max(255, "Company must be at most 255 characters"),
  notes: Yup.string().max(NOTES_MAX, `Notes must be at most ${NOTES_MAX} characters`),
});

/** Find a registered partner by name, mobile or email (the search step of the mobile Add Partner flow). */
const PartnerSearch = ({ service }) => {
  const { values, setValues } = useFormikContext();
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const query = keyword.trim();
    if (query.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setSearched(false);
      setSearchError(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const users = await service.searchPartners(query);
        if (!cancelled) setResults(users);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setSearchError(
            err?.response?.status === 403
              ? "Searching registered partners isn't available for your account. Enter the partner's details below instead."
              : getErrorText(err, "Unable to search partners right now.")
          );
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
          setSearched(true);
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [keyword, service]);

  const select = (user) => {
    setSelectedId(user.id);
    setValues({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      mobile_number: user.mobile_number || "",
      email_address: user.email_address || "",
      company: values.company,
      specialization: values.specialization,
      notes: values.notes,
    });
  };

  // Editing the details afterwards means it is no longer that partner
  const isSelected = (user) => selectedId === user.id && values.mobile_number === (user.mobile_number || "");

  return (
    <Section title="Find a partner" hint="Search registered partners by name, mobile number or email, then pick one to fill in the details below.">
      <div className="fk-field">
        <label className="fk-label" htmlFor="partner-search">
          Search partners
        </label>
        <input
          id="partner-search"
          type="search"
          className="fk-input"
          placeholder={`Type at least ${SEARCH_MIN_CHARS} characters`}
          autoComplete="off"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        />
      </div>

      {searching && (
        <p className="pn-status" role="status">
          <FiSearch aria-hidden="true" /> Searching...
        </p>
      )}
      {searchError && (
        <p className="pn-status text-danger" role="alert">
          {searchError}
        </p>
      )}
      {!searching && !searchError && searched && results.length === 0 && (
        <p className="pn-status" role="status">
          No partner found for "{keyword.trim()}". You can enter their details below instead.
        </p>
      )}
      {results.length > 0 && (
        <ul className="pn-results" aria-label="Matching partners">
          {results.map((user) => (
            <li key={user.id}>
              <button type="button" className="pn-result" aria-pressed={isSelected(user)} onClick={() => select(user)}>
                <PersonAvatar person={user} />
                <span className="pn-result__body">
                  <span className="pn-result__name">{fullName(user)}</span>
                  <span className="pn-result__meta">{[user.mobile_number, user.email_address].filter(Boolean).join(" · ")}</span>
                  {user.cities?.length > 0 && <span className="pn-result__meta">{user.cities.map((c) => c.name).join(", ")}</span>}
                </span>
                {isSelected(user) && <FiCheckCircle className="pn-result__check" size={20} aria-label="Selected" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

/** Lives inside <Formik> so it can read the form state for the unsaved-changes warning. */
const PartnerFormBody = ({ isEdit, service, saved, submitError }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      {submitError && (
        <div className="alert alert-danger pn-alert" role="alert">
          {submitError}
        </div>
      )}

      {!isEdit && <PartnerSearch service={service} />}

      <Section title="Partner details" hint={isEdit ? "Update the partner's contact details." : "Enter the details manually, or pick a partner above."}>
        <div className="fk-grid">
          <TextField name="first_name" label="First name" required placeholder="Enter first name" autoComplete="off" />
          <TextField name="last_name" label="Last name" required placeholder="Enter last name" autoComplete="off" />
          <TextField name="mobile_number" label="Mobile number" required type="tel" inputMode="numeric" placeholder="Enter mobile number" autoComplete="off" />
          <TextField name="email_address" label="Email address" type="email" placeholder="Enter email address" autoComplete="off" />
        </div>
      </Section>

      <Section title="More about this partner" hint="Optional.">
        <div className="fk-grid">
          <TextField name="company" label="Company / firm name" placeholder="Enter company or firm name" autoComplete="off" maxLength={255} />
          <SelectField name="specialization" label="Specialization" options={SPECIALIZATION_OPTIONS} placeholder="Select specialization" />
        </div>
        <TextAreaField name="notes" label="Notes" rows={4} maxLength={NOTES_MAX} placeholder="Anything worth remembering about this partner" />
      </Section>
    </Form>
  );
};

/** /partner/network/partners/new and /partner/network/partners/:Id/edit */
const PartnerFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const seed = useLocation().state?.record;
  const exit = useExitWizard(PARTNERS_HOME);
  const service = useNetworkService();

  // No get-by-id endpoint: the record to edit comes out of the partner list. Adding needs no list at all.
  const load = useCallback(() => (Id ? service.listPartners() : Promise.resolve([])), [Id, service]);
  const { record, loading, error, notFound, refetch } = useListRecord(load, Id, seed, "Unable to load this partner.");

  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (values, { setFieldError }) => {
    setSubmitting(true);
    setSubmitError(null);
    const payload = {
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      mobile_number: values.mobile_number.trim(),
      email_address: values.email_address.trim(),
      company: values.company.trim(),
      specialization: values.specialization,
      notes: values.notes.trim(),
    };
    try {
      if (isEdit) {
        await service.editPartner(record, payload);
      } else {
        await service.addPartner(payload);
        await service.rewardAddPartner();
      }
      showToast(isEdit ? "Partner updated successfully" : "Partner added successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(exit, 0);
    } catch (err) {
      // The form keeps its values so nothing is lost
      const text = getErrorText(err, "Unable to save partner");
      if (isAlreadyAdded(text)) setFieldError("mobile_number", text);
      setSubmitError(text);
      showToast(text, { type: "error" });
      setSubmitting(false);
    }
  };

  if (isEdit && error) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
              Retry
            </button>
            <Link to={PARTNERS_HOME} className="btn btn-sm btn-outline-secondary">
              Back to Partners
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && notFound) {
    return <SaaSNotFoundState title="Partner not found" description="This partner could not be found. It may have been removed." backLink={PARTNERS_HOME} backLabel="Back to Partners" />;
  }

  const initialValues = {
    first_name: record?.first_name || "",
    last_name: record?.last_name || "",
    mobile_number: record?.mobile_number || "",
    email_address: record?.email_address || "",
    company: record?.company || "",
    specialization: record?.specialization || "",
    notes: record?.notes || "",
  };

  return (
    <FormPageLayout
      title={isEdit ? "Edit Partner" : "Add Partner"}
      subtitle={isEdit ? "Update this partner's details." : "Add a partner to your network."}
      formId={FORM_ID}
      submitLabel={isEdit ? "Update Partner" : "Add Partner"}
      submitting={submitting}
      loading={isEdit && loading}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={isEdit ? editSchema : addSchema} onSubmit={handleSubmit}>
        <PartnerFormBody isEdit={isEdit} service={service} saved={saved} submitError={submitError} />
      </Formik>
    </FormPageLayout>
  );
};

export default PartnerFormPage;
